import { FC, useState } from 'react';
import Image, { ImageProps } from 'next/legacy/image';
import { Box, SxProps, Theme } from '@mui/material';
import { clsx } from '@sweatpals/react-ui-kit';

export interface CoreImageProps extends Omit<ImageProps, 'layout' | 'width' | 'height'> {
	width?: string | number;
	height?: string | number;
	wrapperSx?: SxProps<Theme>;
}

/**
 * CoreImage component extends Next.js Image with support for blurred placeholders.
 * It displays a blurred, low-quality preview image while the main image loads,
 * then smoothly fades in the main image once loading completes.
 */
export const CoreImage: FC<CoreImageProps> = props => {
	const { blurDataURL, width, height, wrapperSx, onLoadingComplete, ...imageProps } = props;
	const [isImageLoaded, setIsImageLoaded] = useState(false);

	return (
		<Box
			sx={clsx(
				{
					position: 'relative',
					overflow: 'hidden',
					width,
					height,
				},
				wrapperSx
			)}
		>
			{blurDataURL && (
				<Image
					src={blurDataURL}
					alt="Blured image"
					layout="fill"
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						filter: 'blur(20px)',
						transition: 'opacity 0.3s ease-out',
						opacity: isImageLoaded ? 0 : 1,
					}}
				/>
			)}
			<Image
				{...imageProps}
				layout="fill"
				style={{
					...(blurDataURL ? {
						transition: 'opacity 0.3s ease-in',
						opacity: isImageLoaded ? 1 : 0,
					} : {}),
					...(imageProps.style ?? {}),
				}}
				onLoadingComplete={e => {
					setIsImageLoaded(true);
					onLoadingComplete?.(e);
				}}
			/>
		</Box>
	);
};
